import { RTCClient } from 'webrtc';
import { selectSelectedPair, statsEntries, summarizeInboundVideo, type TRawStat } from 'telemetry/rtcStats';
import {
  recordVoipPeerCount,
  recordVoipPeerSample,
  recordVoipStatsReadFailure,
  type IVoipPeerSample,
} from 'state/session/voipQuality';

/**
 * Prod-safe per-peer `getStats()` sampler feeding the session-scoped VOIP quality counters
 * (AVS-4419, `state/session/voipQuality.ts`).
 *
 * ## Why this exists beside the recorder's sampler
 *
 * `telemetry/rtcStats.ts` already samples `getStats()` — for the Session Flight Recorder, which is
 * built only for dev and prelive (`scripts/recorder-build-gate.mjs`) and reads only while
 * `isRecording()`. The analytics counters this feeds ship from prod, where no recorder exists, so
 * they need a reader that is always present. It reuses the recorder's import-free, pure projection
 * (`selectSelectedPair`, `summarizeInboundVideo`) rather than re-implementing the four-way
 * selected-pair fallback — the same precedent as `utils/web-component.tsx` importing the
 * import-free `telemetry/recorder/gate`. Nothing here imports the recorder itself: the build check
 * greps the built artefact for a sentinel that lives only in `telemetry/recorder/core.ts`.
 *
 * On dev/prelive the two samplers run side by side (≈2N reads per ~4 s on an N-peer call). Accepted:
 * `getStats()` is a cheap asynchronous read, and consulting the recorder from here would drag its
 * sentinel into the prod bundle.
 *
 * ## Cost and privacy
 *
 * N `getStats()` reads per `VOIP_STATS_INTERVAL_MS`, sequential, one per live peer. A peer the
 * recovery ladder wrote off (`transportFailed`) is counted but never read, so a dead peer costs
 * nothing — the ticket's N-not-N² rule. The projection keeps numbers and booleans only: the
 * selected pair's raw addresses and TURN url (`ISelectedPair`) never leave `projectVoipPeerSample`.
 *
 * ## Lifecycle
 *
 * `setTimeout`-rearmed, never `setInterval`, so a pass that outruns its interval cannot stack.
 * Armed from `initWebRtc` after a successful connect and re-armed on every `peer:connected`. Every
 * tick re-resolves `RTCClient.current()` — never a cached client, because `destroy()` nulls the
 * singleton and a takeover rebuilds it — stops itself when there is no client, and parks after
 * `VOIP_STATS_IDLE_TICKS_BEFORE_PARK` peerless ticks. Customers never emit `session:end`
 * (`utils/ui/rtc.ts`), so on their side the stop is the client going away or the idle budget.
 */

/** Healthy cadence. The ticket asks for ~5 s per peer; the recorder's 2-5 s window agrees. */
export const VOIP_STATS_INTERVAL_MS = 5_000;

/** Consecutive peerless ticks after which the loop parks itself until the next `peer:connected`. */
export const VOIP_STATS_IDLE_TICKS_BEFORE_PARK = 3;

/** One peer, as the sampler needs to see it. Read live per tick — never cached by this module. */
export interface IVoipStatsPeer {
  connectionId: string;
  peerConnection: RTCPeerConnection;
  /** The recovery ladder wrote this transport off — counted, never read. */
  transportFailed: boolean;
  /** `stream != null` on the store entry — `ontrack` fired. */
  hasStream: boolean;
  /** The signaled `videoMuted` flag is not `true`. */
  videoExpected: boolean;
}

/** Options for {@link createVoipStatsSampler}. Everything is injected so the loop needs no globals. */
export interface IVoipStatsSamplerOptions {
  /** Resolved LIVE, per tick. `null` means "no client" (stop); `[]` means "a client with no peers" (idle). */
  readPeers: () => IVoipStatsPeer[] | null;
  /** The number of remote peers tracked this tick, written-off ones included. */
  onPeerCount: (remotePeers: number) => void;
  onPeerSample: (connectionId: string, sample: IVoipPeerSample) => void;
  /** A `getStats()` read rejected or was not callable. */
  onReadFailure: () => void;
  intervalMs?: number;
}

/** The sampler's control surface. */
export interface IVoipStatsSampler {
  /** Idempotent. Starts the loop, or un-parks it. */
  arm(): void;
  /** Stops the loop. A read in flight is discarded when it lands. */
  stop(): void;
  /** True while the loop is running. */
  isArmed(): boolean;
}

/**
 * Projects one peer's stats report plus its store-side flags to the sample the counters consume.
 *
 * The one place the raw report is touched. `selectSelectedPair` returns the pair's addresses and
 * TURN url; only the two candidate types (as one boolean) and the RTT survive.
 *
 * @param entries - the plain records of one `RTCStatsReport` (see `statsEntries`)
 * @param flags - the store-side facts the verdict needs, read off the peer entry
 * @returns {IVoipPeerSample} numbers and booleans only
 */
export function projectVoipPeerSample(
  entries: readonly TRawStat[],
  flags: Pick<IVoipStatsPeer, 'hasStream' | 'videoExpected'>
): IVoipPeerSample {
  const pair = selectSelectedPair(entries);

  return {
    relay: pair.localType === 'relay' || pair.remoteType === 'relay',
    roundTripMs: pair.roundTripMs,
    framesDecoded: summarizeInboundVideo(entries).framesDecoded,
    hasStream: flags.hasStream,
    videoExpected: flags.videoExpected,
  };
}

/**
 * The sampler. Pure apart from `setTimeout`; see the module header for the lifecycle it implements.
 *
 * @param options - the injected reader and sinks
 * @returns {IVoipStatsSampler} a parked sampler; call `arm()` to start it
 */
export function createVoipStatsSampler(options: IVoipStatsSamplerOptions): IVoipStatsSampler {
  const intervalMs = options.intervalMs ?? VOIP_STATS_INTERVAL_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let idleTicks = 0;
  let running = false;
  // Bumped on every `arm()`. `running` alone cannot tell two arm-eras apart: `stop()` can land
  // while a read is in flight and a later `arm()` — a fast rejoin, DCC's immediate session
  // restart, a takeover — flips it back to true before that read resolves.
  let armGeneration = 0;

  function schedule(): void {
    if (timer !== null) {
      return;
    }

    timer = setTimeout(() => {
      timer = null;
      const generation = armGeneration;

      // The tick's own `schedule()` is unreachable if anything in it throws — `timer` is already
      // null and every later `arm()` early-returns on `running`, so the loop would be a zombie for
      // the rest of the tab, freezing `statsSamples` and under-reporting the SESSION_ENDED
      // counters. Rescheduling here keeps a surprise from ending the loop; the era guard is the
      // same one the tick uses, so a `stop()` that raced the throw is still respected.
      void tick().catch(() => {
        if (running && armGeneration === generation) {
          schedule();
        }
      });
    }, intervalMs);
  }

  async function tick(): Promise<void> {
    if (!running) {
      return;
    }

    const generation = armGeneration;
    const peers = options.readPeers();

    if (peers === null) {
      stop();
      return;
    }

    if (peers.length === 0) {
      idleTicks += 1;

      if (idleTicks >= VOIP_STATS_IDLE_TICKS_BEFORE_PARK) {
        stop();
        return;
      }

      schedule();
      return;
    }

    idleTicks = 0;
    options.onPeerCount(peers.length);

    for (const peer of peers) {
      if (peer.transportFailed) {
        continue;
      }

      const entries = await readStatsEntries(peer.peerConnection);

      // `stop()` can land while a read is in flight — an uninstall, a session end, a takeover —
      // and a later `arm()` can open a new era before this read resolves. Either way the era this
      // result belongs to is gone: it belongs to nobody, and rescheduling from here would arm a
      // timer nothing owns any more. Comparing the generation, not just `running`, is what keeps
      // the old client's samples out of the new session's tally.
      if (!running || armGeneration !== generation) {
        return;
      }

      if (entries === null) {
        options.onReadFailure();
        continue;
      }

      options.onPeerSample(peer.connectionId, projectVoipPeerSample(entries, peer));
    }

    schedule();
  }

  function stop(): void {
    running = false;
    idleTicks = 0;

    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  return {
    arm(): void {
      if (running) {
        return;
      }

      running = true;
      idleTicks = 0;
      armGeneration += 1;
      schedule();
    },
    stop,
    isArmed(): boolean {
      return running;
    },
  };
}

/**
 * One `getStats()` read that reports its own failure instead of throwing.
 *
 * A pc that closed under the read rejects; a mock or a foreign object has no callable `getStats`.
 * Both are "this read failed", never "this peer is silent" — conflating the two is how a counter
 * invents an outage. Only the call itself is inside the try.
 */
async function readStatsEntries(peerConnection: RTCPeerConnection): Promise<TRawStat[] | null> {
  let report: RTCStatsReport;

  try {
    report = await peerConnection.getStats();
  } catch {
    return null;
  }

  return statsEntries(report);
}

/**
 * The live client, or `null`.
 *
 * `RTCClient.current()` rather than `getInstance()`: an observer must never be the reason a client
 * exists. The `typeof` guard is load-bearing — `utils/ui/rtc.test.ts` mocks `webrtc` as
 * `{ RTCClient: { getInstance } }` with no `current`, and a tick landing inside that suite must read
 * as "no client", never throw inside a timer callback.
 */
function currentClient(): RTCClient | null {
  const current: unknown = RTCClient.current;

  return typeof current === 'function' ? RTCClient.current() : null;
}

/**
 * The LIVE peer list, re-resolved per tick from the current client's store.
 *
 * @returns {IVoipStatsPeer[] | null} `null` when there is no client; otherwise every store entry
 * that holds a peer connection, with the three store-side facts the verdict needs
 */
export function readLiveVoipPeers(): IVoipStatsPeer[] | null {
  const client = currentClient();

  if (!client) {
    return null;
  }

  const remoteConnections = client.store.get('remoteConnections') ?? {};

  return Object.values(remoteConnections).flatMap((connection) =>
    connection.peerConnection
      ? [
          {
            connectionId: connection.connectionId,
            peerConnection: connection.peerConnection,
            transportFailed: !!connection.transportFailed,
            hasStream: !!connection.stream,
            videoExpected: connection.videoMuted !== true,
          },
        ]
      : []
  );
}

/**
 * The one sampler the app runs. Module scope, like `voipHandlerUnsubscribes` in `utils/ui/rtc.ts`:
 * `initWebRtc` arms it after every successful connect and `session:end` stops it, and because it
 * re-resolves the client per tick it follows a takeover's rebuilt client on its own.
 */
export const voipStatsSampler: IVoipStatsSampler = createVoipStatsSampler({
  readPeers: readLiveVoipPeers,
  onPeerCount: recordVoipPeerCount,
  onPeerSample: recordVoipPeerSample,
  onReadFailure: recordVoipStatsReadFailure,
});
